import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Loader2, Lock, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function LoginPage() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password, remember);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(data.message || "Instruções enviadas.");
      setShowForgot(false);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Painel visual */}
      <div className="hidden lg:flex w-1/2 relative bg-brand-sidebar overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1602081115720-72e5b0a254b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Clube Monte Líbano"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D2519] via-[#0D2519]/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-gold flex items-center justify-center text-brand-sidebar font-extrabold font-heading text-xl">ML</div>
            <div>
              <div className="font-heading font-bold tracking-wide">MONTE LÍBANO</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gold">Clube Social</div>
            </div>
          </div>
          <div className="max-w-md">
            <h2 className="font-heading text-4xl font-extrabold leading-tight">Tradição do clube, gestão moderna.</h2>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              Controle financeiro completo e transparente para a administração, tesouraria e diretoria do Clube Monte Líbano.
            </p>
          </div>
          <div className="flex items-center gap-2 text-white/50 text-xs">
            <ShieldCheck className="h-4 w-4 text-gold" /> Ambiente seguro e auditado
          </div>
        </div>
      </div>

      {/* Formulário */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="h-11 w-11 rounded-xl bg-brand flex items-center justify-center text-white font-extrabold font-heading text-xl">ML</div>
            <div className="font-heading font-bold text-brand text-lg">MONTE LÍBANO</div>
          </div>

          {!showForgot ? (
            <>
              <h1 className="font-heading text-2xl font-extrabold text-slate-900">Gestão Financeira</h1>
              <p className="text-sm text-slate-500 mt-1 mb-8">Acesse sua conta para continuar.</p>

              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">E-mail</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      data-testid="login-email-input"
                      className="w-full bg-white border border-slate-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/10 transition-all"
                      placeholder="seu@email.com.br"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password-input"
                      className="w-full bg-white border border-slate-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/10 transition-all"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-remember-checkbox" className="rounded border-slate-300 text-brand focus:ring-brand" />
                    Manter conectado
                  </label>
                  <button type="button" onClick={() => setShowForgot(true)} className="text-brand hover:text-brand-hover font-medium" data-testid="forgot-password-link">
                    Esqueci minha senha
                  </button>
                </div>

                {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}

                <button
                  type="submit" disabled={submitting} data-testid="login-submit-button"
                  className="w-full bg-brand hover:bg-brand-hover text-white font-medium py-2.5 rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Entrar
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-extrabold text-slate-900">Recuperar senha</h1>
              <p className="text-sm text-slate-500 mt-1 mb-8">Informe seu e-mail para receber as instruções.</p>
              <form onSubmit={onForgot} className="space-y-4">
                <input
                  type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/10"
                  placeholder="seu@email.com.br"
                />
                <button type="submit" data-testid="forgot-submit-button" className="w-full bg-brand hover:bg-brand-hover text-white font-medium py-2.5 rounded-lg transition-all">Enviar instruções</button>
                <button type="button" onClick={() => setShowForgot(false)} className="w-full text-slate-500 text-sm hover:text-slate-700">Voltar ao login</button>
              </form>
            </>
          )}

          <p className="text-center text-xs text-slate-400 mt-10">
            Acesso restrito. Usuários são criados por administradores.
          </p>
        </div>
      </div>
    </div>
  );
}
